import { useCallback, useEffect, useRef, useState } from "react";
import { Briefcase, Users, Plus, Loader2, X, Trash2, Star, CalendarClock, FileText, UserPlus, ChevronLeft, Send, CheckCircle2, Download, BarChart3, TrendingUp, Sparkles, Mic, Square, Wand2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, uploadFile } from "../../lib/api";
import { EmployeePicker } from "../../components/os/EmployeePicker";

const STAGE_ORDER = ["nuevo", "screening", "entrevista", "analizando", "oferta", "oferta_aceptada", "contratado", "rechazado", "retirado"];
const STAGE_LABELS = { nuevo: "Nuevo", screening: "Screening", entrevista: "Entrevista", analizando: "Analizando", oferta: "Oferta", oferta_aceptada: "Oferta aceptada", contratado: "Contratado", rechazado: "Rechazado", retirado: "Retirado" };
const STAGE_COLORS = { nuevo: "bg-slate-400", screening: "bg-sky-500", entrevista: "bg-indigo-500", analizando: "bg-amber-500", oferta: "bg-violet-500", oferta_aceptada: "bg-fuchsia-500", contratado: "bg-emerald-500", rechazado: "bg-rose-500", retirado: "bg-navy/40" };

function Modal({ title, onClose, children, wide, testid }) {
  useEffect(() => { const h = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function Recruiting() {
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null);
  const [jobDialog, setJobDialog] = useState(false);
  const [view, setView] = useState("jobs");

  const loadJobs = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/recruiting/jobs").catch(() => ({ data: [] })), api.get("/recruiting/dashboard").catch(() => ({ data: null }))])
      .then(([j, d]) => { setJobs(j.data || []); setDash(d.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { api.get("/recruiting/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  if (activeJob) return <JobBoard meta={meta} job={activeJob} onBack={() => { setActiveJob(null); loadJobs(); }} />;

  return (
    <OSLayout title="OMNIA Reclutamiento">
      <div className="space-y-6" data-testid="recruiting-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {[["jobs", "Vacantes", Briefcase], ["funnel", "Embudo", BarChart3]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`rec-view-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${view === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {view === "funnel" ? <Funnel /> : <>
        {dash && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Vacantes abiertas", dash.open_jobs], ["Candidatos", dash.total_candidates], ["Entrevistas", dash.interviews], ["Contratados", dash.hired]].map(([l, v], i) => (
              <div key={l} className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`rec-kpi-${i}`}>
                <div className="overline text-navy/40">{l}</div><div className="mt-2 text-2xl font-medium">{v}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <h3 className="font-editorial text-2xl flex items-center gap-2"><Briefcase className="w-5 h-5 text-violet" /> Vacantes</h3>
          <button onClick={() => setJobDialog(true)} className="btn btn-primary gap-2" data-testid="rec-new-job"><Plus className="w-4 h-4" /> Nueva vacante</button>
        </div>
        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : jobs.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-8 text-center text-sm text-navy/40">Aún no hay vacantes. Crea la primera.</div>
          : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {jobs.map((j) => (
                <button key={j.id} onClick={() => setActiveJob(j)} className="text-left bg-white border border-navy/10 rounded-2xl p-5 hover:border-violet/40 transition-colors" data-testid={`rec-job-${j.id}`}>
                  <div className="flex items-center justify-between"><span className="font-medium text-navy">{j.titulo}</span><span className={`text-[11px] px-2 py-0.5 rounded-full ${j.estado === "abierta" ? "bg-emerald-500/15 text-emerald-600" : "bg-navy/10 text-navy/40"}`}>{j.estado}</span></div>
                  <div className="text-xs text-navy/45 mt-1">{j.area || "—"} · {j.posiciones} posición(es)</div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-navy/55"><span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {j.candidate_count}</span><span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> {j.hired_count}</span></div>
                </button>
              ))}
            </div>}
        </>}
      </div>
      {jobDialog && <JobDialog onClose={() => setJobDialog(false)} onSaved={() => { setJobDialog(false); loadJobs(); }} />}
    </OSLayout>
  );
}

function Funnel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/recruiting/funnel").then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading || !d) return <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const maxCount = Math.max(1, ...d.funnel.map((f) => f.count));
  return (
    <div className="space-y-6" data-testid="rec-funnel">
      <div className="grid grid-cols-3 gap-4">
        {[["Candidatos", d.total_candidates], ["Contratados", d.hired], ["Conversión", `${d.conversion_rate}%`]].map(([l, v], i) => (
          <div key={l} className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`rec-funnel-kpi-${i}`}><div className="overline text-navy/40">{l}</div><div className="mt-2 text-2xl font-medium">{v}</div></div>
        ))}
      </div>
      <div className="bg-white border border-navy/10 rounded-2xl p-5">
        <div className="text-sm font-medium mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet" /> Embudo por etapa</div>
        <div className="space-y-2">
          {d.funnel.map((f) => (
            <div key={f.stage} className="flex items-center gap-3" data-testid={`rec-funnel-stage-${f.stage}`}>
              <span className="w-28 text-xs text-navy/55">{f.label}</span>
              <div className="flex-1 bg-navy/[0.04] rounded-full h-6 overflow-hidden"><div className="h-full bg-gradient-violet rounded-full flex items-center justify-end px-2 text-[11px] text-white" style={{ width: `${Math.max(6, 100 * f.count / maxCount)}%` }}>{f.count}</div></div>
              <span className="w-16 text-right text-xs text-navy/40">{d.avg_days_per_stage[f.stage] != null ? `${d.avg_days_per_stage[f.stage]}d` : "—"}</span>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-navy/35 mt-2">La columna derecha muestra el tiempo promedio en cada etapa.</div>
      </div>
      <div className="bg-white border border-navy/10 rounded-2xl p-5">
        <div className="text-sm font-medium mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-violet" /> Mejores fuentes</div>
        {d.sources.length === 0 ? <div className="text-sm text-navy/40">Sin datos.</div>
          : <div className="space-y-1.5">{d.sources.map((s) => (
              <div key={s.source} className="flex items-center justify-between text-sm" data-testid={`rec-funnel-source-${s.source}`}>
                <span className="text-navy">{s.source}</span>
                <span className="text-navy/45 text-xs">{s.hired}/{s.total} contratados · {s.conversion}% conversión</span>
              </div>
            ))}</div>}
      </div>
    </div>
  );
}

function JobDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ titulo: "", puesto: "", area: "", departamento: "", equipo: "", posiciones: 1, descripcion: "", estado: "abierta" });
  const [reporta, setReporta] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.titulo.trim()) return toast.error("Escribe el título de la vacante");
    setBusy(true);
    try { await api.post("/recruiting/jobs", { ...f, posiciones: Number(f.posiciones) || 1, reporta_a: reporta?.id || null }); toast.success("Vacante creada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nueva vacante" onClose={onClose} wide testid="rec-job-dialog">
      <div className="space-y-3">
        <input className="field" placeholder="Título de la vacante" value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} data-testid="rec-job-titulo" />
        <div className="grid grid-cols-2 gap-3">
          <input className="field" placeholder="Puesto" value={f.puesto} onChange={(e) => setF({ ...f, puesto: e.target.value })} />
          <input className="field" placeholder="Área" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} />
          <input className="field" placeholder="Departamento" value={f.departamento} onChange={(e) => setF({ ...f, departamento: e.target.value })} />
          <input type="number" min={1} className="field" placeholder="# posiciones" value={f.posiciones} onChange={(e) => setF({ ...f, posiciones: e.target.value })} />
        </div>
        <div><label className="text-xs text-navy/60 mb-1 block">Reporta a (jefe directo)</label><EmployeePicker testid="rec-job-reporta" value={reporta?.id} onChange={setReporta} placeholder="Jefe directo del puesto" /></div>
        <textarea className="field" rows={3} placeholder="Descripción" value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="rec-job-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
    </Modal>
  );
}

function JobBoard({ meta, job, onBack }) {
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/recruiting/candidates?job_id=${job.id}`).then((r) => setCands(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const move = async (cid, stage) => {
    try { await api.post(`/recruiting/candidates/${cid}/stage`, { stage }); load(); if (detail?.id === cid) { const r = await api.get(`/recruiting/candidates?job_id=${job.id}`); setDetail((r.data || []).find((c) => c.id === cid)); } }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const columns = STAGE_ORDER.filter((s) => !["rechazado", "retirado"].includes(s)).concat(["rechazado", "retirado"]);

  return (
    <OSLayout title={`Reclutamiento · ${job.titulo}`}>
      <div className="space-y-4" data-testid="rec-board">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="btn btn-ghost gap-1 text-sm" data-testid="rec-board-back"><ChevronLeft className="w-4 h-4" /> Vacantes</button>
          <div className="flex items-center gap-2">
            <button onClick={() => setCompareOpen(true)} className="btn btn-ghost gap-2" data-testid="rec-compare-open"><Users className="w-4 h-4" /> Comparar con IA</button>
            <button onClick={() => setAddOpen(true)} className="btn btn-primary gap-2" data-testid="rec-add-candidate"><UserPlus className="w-4 h-4" /> Agregar candidato</button>
          </div>
        </div>
        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : <div className="flex gap-3 overflow-x-auto pb-4" data-testid="rec-kanban">
              {columns.map((st) => {
                const list = cands.filter((c) => c.stage === st);
                return (
                  <div key={st} className="min-w-[220px] w-[220px] shrink-0" data-testid={`rec-col-${st}`}>
                    <div className="flex items-center gap-2 mb-2 px-1"><span className={`w-2 h-2 rounded-full ${STAGE_COLORS[st]}`} /><span className="text-sm font-medium text-navy">{STAGE_LABELS[st]}</span><span className="text-xs text-navy/35">{list.length}</span></div>
                    <div className="space-y-2 min-h-[40px]">
                      {list.map((c) => (
                        <button key={c.id} onClick={() => setDetail(c)} className="w-full text-left bg-white border border-navy/10 rounded-xl p-3 hover:border-violet/40 transition-colors" data-testid={`rec-card-${c.id}`}>
                          <div className="text-sm font-medium text-navy truncate">{c.nombre_completo}</div>
                          <div className="text-xs text-navy/40 truncate">{c.email || c.source}</div>
                          {c.expediente_created && <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600">Expediente creado</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>}
      </div>
      {addOpen && <AddCandidate jobId={job.id} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
      {detail && <CandidateDetail meta={meta} job={job} cand={detail} onMove={move} onClose={() => setDetail(null)} onChange={load} setDetail={setDetail} />}
      {compareOpen && <CandidateComparison job={job} onClose={() => setCompareOpen(false)} />}
    </OSLayout>
  );
}

function CandidateComparison({ job, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verdict, setVerdict] = useState(null);
  const [thinking, setThinking] = useState(false);
  const REC = { avanzar: "bg-emerald-500/15 text-emerald-600", en_duda: "bg-amber-500/15 text-amber-600", descartar: "bg-rose-500/15 text-rose-600" };

  useEffect(() => {
    api.get(`/recruiting/jobs/${job.id}/comparison`).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar")).finally(() => setLoading(false));
  }, [job.id]);

  const runVerdict = async () => {
    setThinking(true);
    try { const { data: v } = await api.post(`/recruiting/jobs/${job.id}/ai-shortlist`); setVerdict(v); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el veredicto"); } finally { setThinking(false); }
  };

  const cands = data?.candidates || [];
  return (
    <Modal title={`Comparar candidatos · ${job.titulo}`} onClose={onClose} wide testid="rec-compare-dialog">
      {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : cands.length === 0 ? <div className="text-sm text-navy/40 py-6 text-center">Aún no hay candidatos en esta vacante.</div>
        : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-navy/50">Ordenados por calificación de entrevista. La IA puede recomendar a quién avanzar.</p>
            <button onClick={runVerdict} disabled={thinking} className="btn btn-primary text-xs gap-1.5 shrink-0" data-testid="rec-compare-verdict"><Wand2 className="w-3.5 h-3.5" /> {thinking ? "Analizando…" : "Veredicto IA"}</button>
          </div>

          {verdict && (
            <div className="bg-gradient-to-br from-navy to-navy-soft text-cream rounded-2xl p-4" data-testid="rec-compare-verdict-result">
              <div className="overline text-cream/50 mb-1 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Recomendación de IA</div>
              <div className="text-lg font-medium">Avanzar con: {verdict.recomendado}</div>
              <p className="text-sm text-cream/70 mt-1">{verdict.justificacion}</p>
              {(verdict.ranking || []).length > 0 && <ol className="mt-2 space-y-0.5 text-xs text-cream/80 list-decimal pl-4">{verdict.ranking.map((r, i) => <li key={i}><b>{r.nombre}</b> — {r.veredicto}</li>)}</ol>}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2" data-testid="rec-compare-grid">
            {cands.map((c) => (
              <div key={c.candidate_id} className="border border-navy/10 rounded-xl p-3" data-testid={`rec-compare-card-${c.candidate_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-navy truncate">{c.nombre}</span>
                  {c.avg_score != null && <span className="text-xs flex items-center gap-0.5 text-gold shrink-0"><Star className="w-3.5 h-3.5 fill-gold" /> {c.avg_score}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/50">{c.stage_label}</span>
                  {c.recomendacion && <span className={`text-[10px] px-2 py-0.5 rounded-full ${REC[c.recomendacion] || "bg-navy/10 text-navy/50"}`}>{c.recomendacion}</span>}
                  <span className="text-[10px] text-navy/40">{c.interviews} entrevista(s)</span>
                </div>
                {c.has_ai ? (
                  <>
                    {c.resumen && <p className="text-xs text-navy/60 mt-2 line-clamp-4">{c.resumen}</p>}
                    {c.fortalezas?.length > 0 && <div className="mt-2 text-[11px]"><span className="text-emerald-600 font-medium">Fortalezas:</span> <span className="text-navy/55">{c.fortalezas.slice(0, 3).join("; ")}</span></div>}
                    {c.preocupaciones?.length > 0 && <div className="mt-1 text-[11px]"><span className="text-rose-600 font-medium">Preocupaciones:</span> <span className="text-navy/55">{c.preocupaciones.slice(0, 3).join("; ")}</span></div>}
                  </>
                ) : <p className="text-xs text-navy/35 mt-2 italic">Sin análisis de IA todavía. Genera el "Resumen IA" en una entrevista.</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}


function AddCandidate({ jobId, onClose, onSaved }) {
  const [f, setF] = useState({ nombre: "", apellido: "", email: "", telefono: "", source: "directo", notas: "" });
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [ai, setAi] = useState(null);
  const parseCV = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/recruiting/candidates/parse-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = data.profile || {};
      setAi(p);
      setF((prev) => ({ ...prev, nombre: p.nombre || prev.nombre, apellido: p.apellido || prev.apellido, email: p.email || prev.email, telefono: p.telefono || prev.telefono, source: prev.source === "directo" ? "cv" : prev.source }));
      toast.success("CV analizado con IA");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo leer el CV"); }
    finally { setParsing(false); }
  };
  const save = async () => {
    if (!f.nombre.trim()) return toast.error("Escribe el nombre");
    setBusy(true);
    try { await api.post("/recruiting/candidates", { job_id: jobId, ...f, ai_profile: ai }); toast.success("Candidato agregado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Agregar candidato" onClose={onClose} testid="rec-add-dialog">
      <div className="space-y-3">
        <label className="flex items-center gap-3 border-2 border-dashed border-violet/30 rounded-xl p-4 cursor-pointer hover:bg-violet/5 transition-colors" data-testid="rec-cv-dropzone">
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.docx" className="hidden" onChange={(e) => parseCV(e.target.files?.[0])} data-testid="rec-cv-input" />
          {parsing ? <Loader2 className="w-5 h-5 animate-spin text-violet" /> : <Sparkles className="w-5 h-5 text-violet" />}
          <div><div className="text-sm font-medium text-violet">{parsing ? "Analizando CV con IA…" : "Analizar CV con IA"}</div><div className="text-xs text-navy/50">Sube el CV (PDF, imagen o Word) y precargamos los datos</div></div>
        </label>
        {ai && (
          <div className="bg-violet/5 border border-violet/20 rounded-xl p-3 text-xs" data-testid="rec-cv-preview">
            {ai.resumen && <p className="text-navy/70">{ai.resumen}</p>}
            {ai.anios_experiencia != null && ai.anios_experiencia !== "" && <p className="mt-1 text-navy/60">Experiencia: {ai.anios_experiencia} años</p>}
            {ai.habilidades?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{ai.habilidades.slice(0, 12).map((h, i) => <span key={i} className="bg-white border border-violet/20 text-violet px-2 py-0.5 rounded-full">{h}</span>)}</div>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <input className="field" placeholder="Nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} data-testid="rec-cand-nombre" />
          <input className="field" placeholder="Apellido" value={f.apellido} onChange={(e) => setF({ ...f, apellido: e.target.value })} />
        </div>
        <input className="field" placeholder="Correo (identidad global)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="rec-cand-email" />
        <input className="field" placeholder="Teléfono" value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} />
        <input className="field" placeholder="Fuente (LinkedIn, referido…)" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} />
        <textarea className="field" rows={2} placeholder="Notas" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy || parsing} className="btn btn-primary" data-testid="rec-cand-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agregar"}</button></div>
    </Modal>
  );
}

function CandidateDetail({ meta, job, cand, onMove, onClose, onChange, setDetail }) {
  const [tab, setTab] = useState("pipeline");
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);

  const reloadAll = useCallback(async () => {
    const [iv, of, cs] = await Promise.all([
      api.get(`/recruiting/interviews?candidate_id=${cand.id}`).catch(() => ({ data: [] })),
      api.get(`/recruiting/offers?candidate_id=${cand.id}`).catch(() => ({ data: [] })),
      api.get(`/recruiting/candidates?job_id=${job.id}`).catch(() => ({ data: [] })),
    ]);
    setInterviews(iv.data || []); setOffers(of.data || []);
    const fresh = (cs.data || []).find((c) => c.id === cand.id); if (fresh) setDetail(fresh);
  }, [cand.id, job.id, setDetail]);
  useEffect(() => { reloadAll(); }, [reloadAll]);

  const acceptedOffer = offers.find((o) => o.estado === "aceptada");
  const canHire = cand.stage === "oferta_aceptada" && acceptedOffer;
  const canCreateExp = cand.stage === "contratado" && acceptedOffer && !cand.expediente_created;

  const createExpediente = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/recruiting/candidates/${cand.id}/create-expediente`, {});
      toast.success(r.data.reused_identity ? "Expediente creado (identidad global reutilizada)" : "Expediente e incorporación creados");
      if (r.data.temp_password) toast.info(`Contraseña temporal: ${r.data.temp_password}`, { duration: 15000 });
      reloadAll(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <Modal title={cand.nombre_completo} onClose={onClose} wide testid="rec-detail">
      <div className="flex gap-2 border-b border-navy/10 pb-2 mb-4 text-sm">
        {[["pipeline", "Pipeline"], ["interviews", `Entrevistas (${interviews.length})`], ["offers", `Ofertas (${offers.length})`], ["history", "Historial"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`} data-testid={`rec-detail-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "pipeline" && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-navy/60 mb-1 block">Etapa</label>
            <select className="field" value={cand.stage} onChange={(e) => onMove(cand.id, e.target.value)} data-testid="rec-detail-stage">
              {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="text-sm text-navy/60 space-y-1">
            <div>Correo: {cand.email || "—"}</div><div>Teléfono: {cand.telefono || "—"}</div><div>Fuente: {cand.source}</div>
          </div>
          {canHire && <div className="text-xs bg-fuchsia-500/10 text-fuchsia-700 rounded-lg p-2">La oferta fue aceptada. Cambia la etapa a <b>Contratado</b> para habilitar la creación del expediente.</div>}
          {canCreateExp && (
            <button onClick={createExpediente} disabled={busy} className="btn btn-primary gap-2 w-full" data-testid="rec-create-expediente">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Crear expediente e iniciar incorporación
            </button>
          )}
          {cand.expediente_created && <div className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Expediente e incorporación creados.</div>}
        </div>
      )}

      {tab === "interviews" && <Interviews meta={meta} cand={cand} interviews={interviews} onChange={reloadAll} />}
      {tab === "offers" && <Offers cand={cand} offers={offers} onChange={reloadAll} />}
      {tab === "history" && (
        <div className="space-y-2">
          {(cand.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="text-sm border-l-2 border-violet/30 pl-3 py-0.5">
              <div className="text-navy">{STAGE_LABELS[h.stage] || h.stage} <span className="text-xs text-navy/35">· {h.by_name}</span></div>
              <div className="text-xs text-navy/45">{h.at?.slice(0, 16).replace("T", " ")}{h.comment ? ` · ${h.comment}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function pickMime() {
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg", "audio/mp4"];
  if (typeof MediaRecorder === "undefined") return "";
  return cands.find((c) => MediaRecorder.isTypeSupported(c)) || "";
}

function LiveAssistant({ transcript, setTranscript, onSummary, candName, jobTitle }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const stopFlag = useRef(false);
  const SEG_MS = 12000;

  useEffect(() => () => {
    stopFlag.current = true;
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
  }, []);

  const recordSegment = (stream) => new Promise((resolve) => {
    const chunks = [];
    let mr;
    try { mr = new MediaRecorder(stream, pickMime() ? { mimeType: pickMime() } : undefined); }
    catch { resolve(null); return; }
    recRef.current = mr;
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    mr.onstop = () => resolve(new Blob(chunks, { type: mr.mimeType || "audio/webm" }));
    mr.start();
    setTimeout(() => { try { if (mr.state !== "inactive") mr.stop(); } catch { /* noop */ } }, SEG_MS);
  });

  const transcribeBlob = async (blob) => {
    if (!blob || blob.size < 2500) return;
    setTranscribing(true);
    try {
      const ext = (blob.type || "").includes("ogg") ? "ogg" : (blob.type || "").includes("mp4") ? "mp4" : "webm";
      const fd = new FormData(); fd.append("file", blob, `seg.${ext}`);
      const { data } = await api.post("/recruiting/interviews/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (data.text || "").trim();
      if (t) setTranscript((prev) => (prev ? prev + " " : "") + t);
    } catch { /* silent per segment */ } finally { setTranscribing(false); }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Tu navegador no permite grabar audio");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; stopFlag.current = false; setRecording(true);
      (async () => {
        while (!stopFlag.current) {
          const blob = await recordSegment(stream);
          if (!blob) break;
          if (stopFlag.current) { await transcribeBlob(blob); break; }
          transcribeBlob(blob);
        }
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      })();
    } catch { toast.error("No se pudo acceder al micrófono. Revisa los permisos."); setRecording(false); }
  };

  const stop = () => {
    stopFlag.current = true; setRecording(false);
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch { /* noop */ }
  };

  const summarize = async () => {
    if ((transcript || "").trim().length < 20) return toast.error("Graba la entrevista o escribe la transcripción primero");
    setSummarizing(true);
    try {
      const { data } = await api.post("/recruiting/interviews/summarize", { transcript, candidate_name: candName || "", job_title: jobTitle || "" });
      onSummary(data);
      toast.success("Resumen IA generado y aplicado al formulario");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo resumir"); } finally { setSummarizing(false); }
  };

  return (
    <div className="border border-violet/25 rounded-xl p-3 bg-violet/[0.03] space-y-2" data-testid="rec-iv-assistant">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-violet flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Asistente de entrevista IA</div>
        <div className="flex items-center gap-2">
          {!recording
            ? <button type="button" onClick={start} className="btn btn-ghost text-xs gap-1.5 text-violet" data-testid="rec-iv-rec-start"><Mic className="w-3.5 h-3.5" /> Transcribir en vivo</button>
            : <button type="button" onClick={stop} className="btn btn-ghost text-xs gap-1.5 text-rose-600" data-testid="rec-iv-rec-stop"><Square className="w-3.5 h-3.5" /> Detener</button>}
          <button type="button" onClick={summarize} disabled={summarizing} className="btn btn-primary text-xs gap-1.5" data-testid="rec-iv-summarize">
            {summarizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Resumen IA
          </button>
        </div>
      </div>
      {recording && <div className="flex items-center gap-2 text-xs text-rose-600"><span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Grabando y transcribiendo… (segmentos de ~12s)</div>}
      <textarea className="field text-sm" rows={4} placeholder="La transcripción aparece aquí en vivo. También puedes escribir o pegar notas de la conversación." value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="rec-iv-transcript" />
      <div className="text-[11px] text-navy/40">{transcribing ? "Transcribiendo segmento…" : `${(transcript || "").trim().split(/\s+/).filter(Boolean).length} palabras`} · La IA sugerirá resumen, calificación y recomendación.</div>
    </div>
  );
}

function Interviews({ meta, cand, interviews, onChange }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState({ fecha: "", hora: "", tipo: "video", notas: "", calificacion: "", recomendacion: "avanzar", estado: "programada" });
  const [entrevistador, setEntrevistador] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiSummary, setAiSummary] = useState(null);

  const resetForm = () => {
    setF({ fecha: "", hora: "", tipo: "video", notas: "", calificacion: "", recomendacion: "avanzar", estado: "programada" });
    setEntrevistador(null); setFiles([]); setTranscript(""); setAiSummary(null); setEditingId(null);
  };

  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (iv) => {
    setEditingId(iv.id);
    setF({ fecha: iv.fecha || "", hora: iv.hora || "", tipo: iv.tipo || "video", notas: iv.notas || "",
           calificacion: iv.calificacion != null ? String(iv.calificacion) : "",
           recomendacion: iv.recomendacion || "avanzar", estado: iv.estado || "programada" });
    setEntrevistador(iv.entrevistador_id ? { id: iv.entrevistador_id, name: iv.entrevistador_name } : null);
    setFiles(iv.files || []); setTranscript(iv.transcript || ""); setAiSummary(iv.ai_summary || null);
    setOpen(true);
  };

  const applySummary = (s) => {
    setAiSummary(s);
    const preguntas = (s.preguntas_sugeridas || []).length ? `\n\nPreguntas sugeridas:\n- ${(s.preguntas_sugeridas || []).join("\n- ")}` : "";
    setF((prev) => ({
      ...prev,
      notas: (s.resumen || prev.notas) + preguntas,
      calificacion: s.calificacion != null ? String(s.calificacion) : prev.calificacion,
      recomendacion: s.recomendacion || prev.recomendacion,
      estado: prev.estado === "programada" ? "completada" : prev.estado,
    }));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const up = await uploadFile(file, true); setFiles((p) => [...p, { name: file.name, path: up.path }]); toast.success("Archivo adjuntado"); }
    catch { toast.error("No se pudo subir"); }
  };
  const save = async () => {
    if (!entrevistador) return toast.error("Selecciona el entrevistador");
    setBusy(true);
    try {
      const payload = { ...f, calificacion: f.calificacion ? Number(f.calificacion) : null, files, transcript, ai_summary: aiSummary };
      if (editingId) {
        await api.patch(`/recruiting/interviews/${editingId}`, { ...payload, entrevistador_id: entrevistador.id });
        toast.success("Entrevista actualizada");
      } else {
        await api.post("/recruiting/interviews", { candidate_id: cand.id, entrevistador_id: entrevistador.id, ...payload });
        toast.success("Entrevista registrada");
      }
      setOpen(false); resetForm(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/recruiting/interviews/${id}`); onChange(); } catch { toast.error("Error"); } };

  return (
    <div className="space-y-3">
      <button onClick={openCreate} className="btn btn-ghost gap-1 text-sm" data-testid="rec-add-interview"><Plus className="w-4 h-4" /> Programar entrevista</button>
      {interviews.map((iv) => (
        <div key={iv.id} className="border border-navy/10 rounded-xl p-3 hover:border-violet/40 transition-colors cursor-pointer" onClick={() => openEdit(iv)} data-testid={`rec-interview-${iv.id}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{iv.entrevistador_name} <span className="text-xs text-navy/40">· {iv.tipo}</span></div>
            <div className="flex items-center gap-2">
              {iv.calificacion != null && <span className="text-xs flex items-center gap-0.5 text-gold"><Star className="w-3.5 h-3.5 fill-gold" /> {iv.calificacion}</span>}
              <button onClick={(e) => { e.stopPropagation(); openEdit(iv); }} className="text-navy/30 hover:text-violet" data-testid={`rec-interview-edit-${iv.id}`}><Wand2 className="w-3.5 h-3.5" /></button>
              <button onClick={(e) => { e.stopPropagation(); del(iv.id); }} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="text-xs text-navy/45 mt-0.5"><CalendarClock className="w-3 h-3 inline mr-1" />{iv.fecha} {iv.hora} · {iv.recomendacion || "—"} · {iv.estado}</div>
          {iv.notas && <p className="text-sm text-navy/55 mt-1 whitespace-pre-wrap line-clamp-3">{iv.notas}</p>}
          {(iv.transcript || iv.ai_summary) && <div className="text-xs text-violet mt-1 flex items-center gap-1" data-testid={`rec-interview-ai-${iv.id}`}><Sparkles className="w-3 h-3" /> Transcripción y análisis IA · toca para editar</div>}
          {(iv.files || []).length > 0 && <div className="text-xs text-violet mt-1">{iv.files.length} archivo(s) adjunto(s)</div>}
        </div>
      ))}
      {open && (
        <Modal title={editingId ? "Editar entrevista · Asistente IA" : "Entrevista · Asistente IA"} onClose={() => { setOpen(false); resetForm(); }} wide testid="rec-interview-dialog">
          <div className="space-y-3">
            <div><label className="text-xs text-navy/60 mb-1 block">Entrevistador</label><EmployeePicker testid="rec-iv-user" value={entrevistador?.id} onChange={setEntrevistador} placeholder="¿Quién entrevista?" /></div>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" className="field" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} data-testid="rec-iv-fecha" />
              <input type="time" className="field" value={f.hora} onChange={(e) => setF({ ...f, hora: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className="field" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>{meta.interview_types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select className="field" value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })}><option value="programada">Programada</option><option value="analizando">Analizando</option><option value="completada">Completada</option></select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" min={0} max={10} step={0.5} className="field" placeholder="Calificación (0-10)" value={f.calificacion} onChange={(e) => setF({ ...f, calificacion: e.target.value })} data-testid="rec-iv-score" />
              <select className="field" value={f.recomendacion} onChange={(e) => setF({ ...f, recomendacion: e.target.value })}><option value="avanzar">Avanzar</option><option value="en_duda">En duda</option><option value="descartar">Descartar</option></select>
            </div>

            <LiveAssistant transcript={transcript} setTranscript={setTranscript} onSummary={applySummary} candName={cand.nombre_completo} jobTitle={""} />
            {aiSummary && (
              <div className="bg-violet/5 border border-violet/20 rounded-xl p-3 text-xs space-y-2" data-testid="rec-iv-ai-summary">
                {aiSummary.fortalezas?.length > 0 && <div><div className="font-medium text-emerald-600 mb-0.5">Fortalezas</div><ul className="list-disc pl-4 text-navy/60 space-y-0.5">{aiSummary.fortalezas.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                {aiSummary.preocupaciones?.length > 0 && <div><div className="font-medium text-rose-600 mb-0.5">Preocupaciones</div><ul className="list-disc pl-4 text-navy/60 space-y-0.5">{aiSummary.preocupaciones.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              </div>
            )}

            <textarea className="field" rows={4} placeholder="Notas de la entrevista (la IA las precarga con el resumen)" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} data-testid="rec-iv-notas" />
            <label className="btn btn-ghost cursor-pointer gap-2 text-sm w-fit">Adjuntar archivo<input type="file" className="hidden" onChange={onFile} /></label>
            {files.length > 0 && <div className="text-xs text-navy/50">{files.map((x) => x.name).join(", ")}</div>}
          </div>
          <div className="flex justify-end gap-2 mt-5"><button onClick={() => { setOpen(false); resetForm(); }} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="rec-iv-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Guardar cambios" : "Guardar")}</button></div>
        </Modal>
      )}
    </div>
  );
}

function Offers({ cand, offers, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ sueldo: "", moneda: "MXN", tipo_sueldo: "mensual", fecha_ingreso: "", puesto: "", notas: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.sueldo) return toast.error("Indica el sueldo");
    setBusy(true);
    try { await api.post("/recruiting/offers", { candidate_id: cand.id, ...f, sueldo: Number(f.sueldo) }); toast.success("Oferta generada"); setOpen(false); setF({ sueldo: "", moneda: "MXN", tipo_sueldo: "mensual", fecha_ingreso: "", puesto: "", notas: "" }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const status = async (oid, estado) => { try { await api.post(`/recruiting/offers/${oid}/status`, { estado }); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const requestApproval = async (oid) => { try { await api.post(`/recruiting/offers/${oid}/request-approval`); toast.success("Enviada a aprobación"); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (oid) => { try { await api.delete(`/recruiting/offers/${oid}`); onChange(); } catch { toast.error("Error"); } };

  const OFFER_ST = { borrador: "bg-navy/10 text-navy/50", enviada: "bg-sky-500/15 text-sky-600", aprobada: "bg-violet-500/15 text-violet-700", aceptada: "bg-emerald-500/15 text-emerald-600", rechazada: "bg-destructive/15 text-destructive" };
  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(true)} className="btn btn-ghost gap-1 text-sm" data-testid="rec-add-offer"><Plus className="w-4 h-4" /> Generar oferta</button>
      {offers.map((o) => (
        <div key={o.id} className="border border-navy/10 rounded-xl p-3" data-testid={`rec-offer-${o.id}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-violet" /> ${o.sueldo.toLocaleString()} {o.moneda} <span className="text-xs text-navy/40">/ {o.tipo_sueldo}</span></div>
            <div className="flex items-center gap-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${OFFER_ST[o.estado]}`}>{o.estado}</span><button onClick={() => del(o.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
          </div>
          <div className="text-xs text-navy/45 mt-0.5">Ingreso: {o.fecha_ingreso || "—"} · {o.puesto || "—"}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {o.estado === "borrador" && <button onClick={() => requestApproval(o.id)} className="btn btn-ghost text-xs gap-1" data-testid={`rec-offer-approve-${o.id}`}><Send className="w-3.5 h-3.5" /> Enviar a aprobación</button>}
            {(o.estado === "borrador" || o.estado === "enviada") && <button onClick={() => status(o.id, "aprobada")} className="btn btn-ghost text-xs">Marcar aprobada</button>}
            {o.estado === "aprobada" && <button onClick={() => status(o.id, "aceptada")} className="btn btn-primary text-xs" data-testid={`rec-offer-accept-${o.id}`}>Candidato aceptó</button>}
            {o.estado !== "aceptada" && o.estado !== "rechazada" && <button onClick={() => status(o.id, "rechazada")} className="btn btn-ghost text-xs text-destructive">Rechazada</button>}
          </div>
        </div>
      ))}
      {open && (
        <Modal title="Generar oferta" onClose={() => setOpen(false)} testid="rec-offer-dialog">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="number" className="field" placeholder="Sueldo" value={f.sueldo} onChange={(e) => setF({ ...f, sueldo: e.target.value })} data-testid="rec-offer-sueldo" />
              <select className="field" value={f.tipo_sueldo} onChange={(e) => setF({ ...f, tipo_sueldo: e.target.value })}><option value="mensual">Mensual</option><option value="quincenal">Quincenal</option><option value="por_hora">Por hora</option></select>
            </div>
            <input type="date" className="field" value={f.fecha_ingreso} onChange={(e) => setF({ ...f, fecha_ingreso: e.target.value })} data-testid="rec-offer-fecha" />
            <input className="field" placeholder="Puesto ofrecido" value={f.puesto} onChange={(e) => setF({ ...f, puesto: e.target.value })} data-testid="rec-offer-puesto" />
            <textarea className="field" rows={2} placeholder="Notas" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="rec-offer-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar"}</button></div>
        </Modal>
      )}
    </div>
  );
}
